import React, { useEffect, useState } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import { Link, useNavigate } from "react-router-dom";
import {
  GetMarketingSchool,
  MarketingSchoolDelete,
} from "../../../api/MarketinService";
import Swal from "sweetalert2";
import { SweetAlertShow, LoadingDiv } from "../../master/Master";
import { getSessions } from "../../../api/SessionsService";
import MarketingHeader from "./MarketingHeader";
import { setPrintContent } from "../../../PrintStore";
import usePrivateLink from "../../config/usePrivateLink";

const Marketings = () => {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({});
  const [session, setSession] = useState("");

  const navigate = useNavigate();

  /* =========================================================
     SESSION
  ========================================================= */

  const getSession = async () => {
    setLoading(true);

    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const activeSession = response?.data?.data?.find(
          (s) => s?.status === "1",
        );

        setFilters((prev) => ({
          ...prev,
          session: activeSession?.id || "",
        }));

        setSession(response?.data);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch sessions.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     FETCH SOURCES
  ========================================================= */

  const fetchSource = async () => {
    setLoading(true);

    let addUrl = "";

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value) {
        addUrl += `&${key}=${encodeURIComponent(value)}`;
      }
    });

    try {
      const response = await GetMarketingSchool(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || []);
      } else {
        setData([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to fetch sources.",
        );
      }
    } catch (error) {
      setData([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSource();
  }, [filters]);

  useEffect(() => {
    getSession();
  }, []);

  /* =========================================================
     DELETE SOURCE
  ========================================================= */

  const deleteSourse = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action will permanently delete the source!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await MarketingSchoolDelete(id);

          if (response?.data?.status) {
            SweetAlertShow("success", "Successfully", response?.data?.message);

            fetchSource();
          } else {
            SweetAlertShow("info", "Warning", response?.data?.message);
          }
        } catch (err) {
          SweetAlertShow(
            "error",
            "Error",
            err?.response?.data?.message ?? err?.message,
          );
        }
      }
    });
  };

  /* =========================================================
     FILTER HANDLER
  ========================================================= */

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    const activeSession = session?.data?.find((s) => s?.status === "1");

    setFilters({
      session: activeSession?.id || "",
    });
  };

  /* =========================================================
     PRINT
  ========================================================= */

  const handlePrint = () => {
    const printTable = (
      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-primary">
            <caption className="text-center fw-bold fs-5 mb-2 w-100">
              Source List
            </caption>

            <tr>
              <th>#</th>
              <th>Source Name</th>
              <th>Contact No</th>
              <th>Contact Person</th>
              <th>Source Type</th>
              <th>Email</th>
              <th>Address</th>
            </tr>
          </thead>

          <tbody className="text-capitalize">
            {data?.map((sch, index) => (
              <tr key={index}>
                <td>{index + 1}</td>

                <td>{sch?.source_name || "N/A"}</td>

                <td>{sch?.contact_no || "N/A"}</td>

                <td>{sch?.contact_person || "N/A"}</td>

                <td>{sch?.source_type || "N/A"}</td>

                <td>{sch?.email || "N/A"}</td>

                <td>
                  {[sch?.address, sch?.district, sch?.block, sch?.state]
                    .filter(Boolean)
                    .join(", ") || "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    setPrintContent(printTable);
    navigate("/print");
  };

  /* =========================================================
     SOURCE TYPE BADGE
  ========================================================= */

  const getSourceTypeClass = (type) => {
    switch (type?.toLowerCase()) {
      case "school":
        return "bg-primary-subtle text-primary";

      case "coaching":
        return "bg-warning-subtle text-warning";

      case "college":
        return "bg-info-subtle text-info";

      case "other":
      case "person":
        return "bg-success-subtle text-success";

      default:
        return "bg-secondary-subtle text-secondary";
    }
  };

  const getSourceTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "school":
        return "ri-school-line";

      case "coaching":
        return "ri-book-open-line";

      case "college":
        return "ri-building-4-line";

      case "other":
      case "person":
        return "ri-user-line";

      default:
        return "ri-price-tag-3-line";
    }
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          {/* SIDEBAR */}
          <Header />

          <div className="layout-page">
            {/* NAVBAR */}
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid py-3">
                <div className="row g-4">
                  {/* =====================================================
                      MARKETING HEADER
                  ====================================================== */}

                  <div className="col-12">
                    <MarketingHeader />
                  </div>

                  {/* =====================================================
                      PAGE HEADER + FILTERS
                  ====================================================== */}

                  <div className="col-12">
                    <div className="card border-0 shadow-sm overflow-hidden">
                      {/* HEADER */}
                      <div className="card-header bg-transparent border-bottom px-4 py-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            {/* HEADER ICON */}

                            <div
                              className="rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: "52px",
                                height: "52px",
                              }}
                            >
                              <i className="ri-price-tag-3-line fs-3"></i>
                            </div>

                            {/* TITLE */}

                            <div>
                              <h4 className="mb-1 fw-bold">
                                Marketing Sources
                              </h4>

                              <p className="mb-0 text-muted">
                                Manage schools, coaching centres, colleges and
                                other marketing sources
                              </p>
                            </div>
                          </div>

                          {/* ACTIONS */}

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {hasAccess("/marketing/print") && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2"
                                onClick={handlePrint}
                                disabled={!data?.length}
                                title="Print source list"
                              >
                                <i className="ri-printer-line"></i>
                                <span>Print</span>
                              </button>
                            )}

                            {hasAccess("/marketing/add_school") && (
                              <Link
                                to="/marketing/add_school"
                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                              >
                                <i className="ri-add-line"></i>
                                <span>Add New Source</span>
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* =====================================================
                          FILTER SECTION
                      ====================================================== */}

                      <div className="card-body px-4 py-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-primary">
                              <i className="ri-filter-3-line fs-5"></i>
                            </span>

                            <div>
                              <h6 className="mb-0 fw-bold">Filters</h6>

                              <small className="text-muted">
                                Narrow down the source records
                              </small>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                            onClick={clearFilters}
                          >
                            <i className="ri-refresh-line"></i>
                            Reset
                          </button>
                        </div>

                        <div className="border rounded-3 p-3">
                          <div className="row g-3">
                            {/* SESSION */}

                            <div className="col-lg-4 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  name="session"
                                  value={filters?.session || ""}
                                  onChange={(e) =>
                                    handleFilterChange(
                                      "session",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">Select Session</option>

                                  {session?.data?.map((sess) => (
                                    <option key={sess.id} value={sess.id}>
                                      {sess.session} :{" "}
                                      {sess.status === "1"
                                        ? "Active"
                                        : "Inactive"}
                                    </option>
                                  ))}
                                </select>

                                <label>
                                  <i className="ri-calendar-line me-1"></i>
                                  Select Session
                                </label>
                              </div>
                            </div>

                            {/* SOURCE NAME */}

                            <div className="col-lg-4 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  className="form-control input-uppercase"
                                  name="source_name"
                                  placeholder="Source Name"
                                  value={filters?.source_name || ""}
                                  onChange={(e) =>
                                    handleFilterChange(
                                      "source_name",
                                      e.target.value,
                                    )
                                  }
                                />

                                <label>
                                  <i className="ri-search-line me-1"></i>
                                  Source Name
                                </label>
                              </div>
                            </div>

                            {/* ADDRESS */}

                            <div className="col-lg-4 col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="text"
                                  className="form-control"
                                  name="area"
                                  placeholder="Search Address"
                                  value={filters?.area || ""}
                                  onChange={(e) =>
                                    handleFilterChange("area", e.target.value)
                                  }
                                />

                                <label>
                                  <i className="ri-map-pin-line me-1"></i>
                                  Address
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =====================================================
                      SOURCE LIST
                  ====================================================== */}

                  <div className="col-12">
                    <div className="card border-0 shadow-sm overflow-hidden">
                      {/* TABLE HEADER */}

                      <div className="card-header bg-transparent border-bottom px-4 py-3">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-3 bg-success-subtle text-success d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{
                                width: "46px",
                                height: "46px",
                              }}
                            >
                              <i className="ri-list-check-2 fs-4"></i>
                            </div>

                            <div>
                              <h5 className="mb-1 fw-bold">Source List</h5>

                              <small className="text-muted">
                                All available marketing sources
                              </small>
                            </div>
                          </div>

                          {/* COUNT */}

                          <span className="badge bg-secondary-subtle text-secondary rounded-pill px-3 py-2">
                            <i className="ri-database-2-line me-1"></i>
                            {data?.length || 0}{" "}
                            {data?.length === 1 ? "Source" : "Sources"}
                          </span>
                        </div>
                      </div>

                      {/* TABLE */}

                      <div className="card-body p-0">
                        {data?.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th
                                    className="ps-4"
                                    style={{ width: "70px" }}
                                  >
                                    #
                                  </th>

                                  <th>
                                    <i className="ri-price-tag-3-line me-1"></i>
                                    Source
                                  </th>

                                  <th>
                                    <i className="ri-phone-line me-1"></i>
                                    Contact
                                  </th>

                                  <th>
                                    <i className="ri-user-line me-1"></i>
                                    Contact Person
                                  </th>

                                  <th>
                                    <i className="ri-building-line me-1"></i>
                                    Type
                                  </th>

                                  <th>
                                    <i className="ri-mail-line me-1"></i>
                                    Email
                                  </th>

                                  <th>
                                    <i className="ri-map-pin-line me-1"></i>
                                    Address
                                  </th>

                                  <th
                                    className="text-center pe-4"
                                    style={{ width: "130px" }}
                                  >
                                    Actions
                                  </th>
                                </tr>
                              </thead>

                              <tbody className="text-capitalize">
                                {data?.map((sch, index) => (
                                  <tr key={sch?.id || index}>
                                    {/* NUMBER */}

                                    <td className="ps-4">
                                      <span className="text-muted fw-semibold">
                                        {index + 1}
                                      </span>
                                    </td>

                                    {/* SOURCE */}

                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <div
                                          className="rounded-2 bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0"
                                          style={{
                                            width: "38px",
                                            height: "38px",
                                          }}
                                        >
                                          <i
                                            className={`${getSourceTypeIcon(
                                              sch?.source_type,
                                            )}`}
                                          ></i>
                                        </div>

                                        <div>
                                          <div className="fw-semibold text-capitalize">
                                            {sch?.source_name || "N/A"}
                                          </div>

                                          {sch?.id && (
                                            <small className="text-muted">
                                              ID: {sch.id}
                                            </small>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* CONTACT */}

                                    <td>
                                      {sch?.contact_no ? (
                                        <a
                                          href={`tel:${sch.contact_no}`}
                                          className="text-decoration-none d-inline-flex align-items-center gap-1"
                                        >
                                          <i className="ri-phone-line"></i>
                                          {sch.contact_no}
                                        </a>
                                      ) : (
                                        <span className="text-muted">N/A</span>
                                      )}
                                    </td>

                                    {/* CONTACT PERSON */}

                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <span
                                          className="rounded-circle bg-secondary-subtle text-secondary d-inline-flex align-items-center justify-content-center"
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                          }}
                                        >
                                          <i className="ri-user-line"></i>
                                        </span>

                                        <span>
                                          {sch?.contact_person || "N/A"}
                                        </span>
                                      </div>
                                    </td>

                                    {/* TYPE */}

                                    <td>
                                      <span
                                        className={`badge rounded-pill px-3 py-2 d-inline-flex align-items-center gap-1 ${getSourceTypeClass(
                                          sch?.source_type,
                                        )}`}
                                      >
                                        <i
                                          className={getSourceTypeIcon(
                                            sch?.source_type,
                                          )}
                                        ></i>

                                        {sch?.source_type || "N/A"}
                                      </span>
                                    </td>

                                    {/* EMAIL */}

                                    <td>
                                      {sch?.email ? (
                                        <a
                                          href={`mailto:${sch.email}`}
                                          className="text-decoration-none"
                                        >
                                          <i className="ri-mail-line me-1"></i>
                                          {sch.email}
                                        </a>
                                      ) : (
                                        <span className="text-muted">N/A</span>
                                      )}
                                    </td>

                                    {/* ADDRESS */}

                                    <td style={{ minWidth: "200px" }}>
                                      <div className="d-flex align-items-start gap-2">
                                        <i className="ri-map-pin-2-line text-muted mt-1"></i>

                                        <span className="text-muted small text-capitalize">
                                          {[
                                            sch?.address,
                                            sch?.district,
                                            sch?.block,
                                            sch?.state,
                                          ]
                                            .filter(Boolean)
                                            .join(", ") || "N/A"}
                                        </span>
                                      </div>
                                    </td>

                                    {/* ACTIONS */}

                                    <td className="text-center pe-4">
                                      <div className="d-inline-flex align-items-center gap-1">
                                        {/* VIEW / STUDENTS */}

                                        {/* {hasAccess(
                                          "/marketing/update_school",
                                        ) && (
                                          <Link
                                            to={`/marketing/add_student/source?id=${sch.id}&name=${sch.source_name}`}
                                            className="btn btn-sm btn-icon btn-outline-success"
                                            title="View Students"
                                          >
                                            <i className="ri-eye-line"></i>
                                          </Link>
                                        )} */}

                                        {/* EDIT */}

                                        {hasAccess(
                                          "/marketing/update_school",
                                        ) && (
                                            <Link
                                              to={`/marketing/update_school?id=${sch.id}`}
                                              className="btn btn-sm btn-icon btn-outline-primary"
                                              title="Edit Source"
                                            >
                                              <i className="ri-pencil-line"></i>
                                            </Link>
                                          )}

                                        {/* DELETE */}

                                        {hasAccess("/marketing/delete") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-outline-danger"
                                            title="Delete Source"
                                            onClick={() =>
                                              Swal.fire({
                                                title: "Are you sure?",
                                                text: "This action will permanently delete the source!",
                                                icon: "warning",
                                                showCancelButton: true,
                                                confirmButtonColor: "#d33",
                                                cancelButtonColor: "#6c757d",
                                                confirmButtonText:
                                                  "Yes, delete it!",
                                                cancelButtonText: "Cancel",
                                              }).then(async (result) => {
                                                if (result.isConfirmed) {
                                                  deleteSourse(sch?.id);
                                                }
                                              })
                                            }
                                          >
                                            <i className="ri-delete-bin-6-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          /* =================================================
                             EMPTY STATE
                          ================================================== */

                          <div className="text-center py-5 px-3">
                            <div
                              className="rounded-circle bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center mx-auto mb-3"
                              style={{
                                width: "72px",
                                height: "72px",
                              }}
                            >
                              <i className="ri-inbox-archive-line fs-2"></i>
                            </div>

                            <h5 className="fw-bold mb-2">No Sources Found</h5>

                            <p className="text-muted mb-4">
                              No marketing sources match the selected filters.
                            </p>

                            {hasAccess("/marketing/add_school") && (
                              <Link
                                to="/marketing/add_school"
                                className="btn btn-primary d-inline-flex align-items-center gap-2"
                              >
                                <i className="ri-add-line"></i>
                                Add New Source
                              </Link>
                            )}
                          </div>
                        )}
                      </div>

                      {/* TABLE FOOTER */}

                      {data?.length > 0 && (
                        <div className="card-footer bg-transparent border-top px-4 py-3">
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <small className="text-muted">
                              Showing <strong>{data?.length || 0}</strong>{" "}
                              source
                              {data?.length === 1 ? "" : "s"}
                            </small>

                            <small className="text-muted">
                              <i className="ri-information-line me-1"></i>
                              Use the action buttons to manage sources.
                            </small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="content-backdrop fade"></div>
            </div>
          </div>

          <div className="layout-overlay layout-menu-toggle"></div>
          <div className="drag-target"></div>
        </div>
      </div>
    </>
  );
};

export default Marketings;
